import { useCallback, useRef } from 'react';

/**
 * Реестр DOM-контейнеров заметок.
 *
 * Искать контейнер по атрибуту в разметке нельзя: содержимое заметки
 * рендерится сырым HTML, внутри него может оказаться какой угодно атрибут, и
 * правит это содержимое кто угодно — в том числе LLM через MCP. Подделка выдала
 * бы себя за видимую заметку и увела бы переход не туда. Поэтому в реестр
 * попадает только то, что зарегистрировал сам React-компонент.
 *
 * Состояние модульное, а не в контексте: реестр описывает DOM, который и сам
 * глобален, а провайдер понадобился бы в двух далёких друг от друга поддеревьях
 * ради одной карты.
 */
const containers = new Map<string, HTMLElement[]>();

export function registerNoteContainer(noteId: string, element: HTMLElement): () => void {
  const list = containers.get(noteId) ?? [];

  list.push(element);
  containers.set(noteId, list);

  return () => {
    const current = containers.get(noteId);

    if (!current) {
      return;
    }

    const next = current.filter((item) => item !== element);

    if (next.length === 0) {
      containers.delete(noteId);

      return;
    }

    containers.set(noteId, next);
  };
}

/**
 * Контейнеры заметки в порядке регистрации.
 *
 * Отсеиваются вынутые из документа: компонент мог не успеть снять регистрацию,
 * а переход к метке в мёртвом узле выглядел бы как «ничего не произошло».
 */
export function getNoteContainers(noteId: string): HTMLElement[] {
  return (containers.get(noteId) ?? []).filter((element) => element.isConnected);
}

/**
 * Ref-колбэк для контейнера заметки: регистрирует узел при монтировании и
 * снимает регистрацию при размонтировании или смене заметки.
 */
export function useNoteContainer(noteId: string) {
  const unregisterRef = useRef<(() => void) | null>(null);

  return useCallback(
    (element: HTMLElement | null) => {
      unregisterRef.current?.();
      unregisterRef.current = element ? registerNoteContainer(noteId, element) : null;
    },
    [noteId],
  );
}
